class SitePlatformTabs extends HTMLElement {
  #controller;
  #tabs = [];
  #panels = [];

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#tabs = [...this.querySelectorAll('[data-platform-tab]')];
    this.#panels = [...this.querySelectorAll('[data-platform-panel]')];
    const { signal } = this.#controller;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    const activeIndex = this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#select(activeIndex < 0 ? 0 : activeIndex);
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  #onClick = (event) => {
    const tab = event.target.closest('[data-platform-tab]');
    const index = this.#tabs.indexOf(tab);
    if (index !== -1) this.#select(index);
  };

  #onKeyDown = (event) => {
    const index = this.#tabs.indexOf(event.target);
    if (index === -1) return;

    let nextIndex;
    switch (event.key) {
      case 'ArrowDown':
        nextIndex = (index + 1) % this.#tabs.length;
        break;
      case 'ArrowUp':
        nextIndex = (index - 1 + this.#tabs.length) % this.#tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#select(nextIndex);
    this.#tabs[nextIndex].focus();
  };

  #onBlockSelect = (event) => {
    const index = this.#tabs.findIndex((tab) => tab.dataset.blockId === event.detail?.blockId);
    if (index !== -1) this.#select(index);
  };

  #select(index) {
    if (!this.#tabs[index]) return;

    this.#tabs.forEach((tab, tabIndex) => {
      const active = tabIndex === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
    });

    this.#panels.forEach((panel, panelIndex) => {
      const isVisible = panelIndex === index;
      panel.hidden = !isVisible;
      const carousel = panel.querySelector('site-warehouse-carousel');
      if (carousel) {
        if (isVisible) {
          carousel.startAutoplay?.();
        } else {
          carousel.pauseAutoplay?.();
        }
      }
    });
  }
}

if (!customElements.get('site-platform-tabs')) {
  customElements.define('site-platform-tabs', SitePlatformTabs);
}

class SiteWarehouseCarousel extends HTMLElement {
  #controller;
  #track = null;
  #slides = [];
  #index = 0;
  #timer = null;
  #touchStartX = null;
  #touchStartY = null;
  #isRtl = false;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#track = this.querySelector('[data-warehouse-track]');
    this.#slides = [...this.querySelectorAll('[data-warehouse-slide]')];
    this.#isRtl = getComputedStyle(this).direction === 'rtl';

    const activeSlideIndex = this.#slides.findIndex(
      (slide) => slide.classList.contains('is-active') || !slide.hidden
    );
    this.#index = Math.max(0, activeSlideIndex);

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.addEventListener('mouseenter', this.#onMouseEnter, { signal });
    this.addEventListener('mouseleave', this.#onMouseLeave, { signal });
    this.addEventListener('focusin', this.#onFocusIn, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });
    this.addEventListener('touchstart', this.#onTouchStart, { signal, passive: true });
    this.addEventListener('touchend', this.#onTouchEnd, { signal, passive: true });

    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });

    this.#show(this.#index, { immediate: true });

    const parentPanel = this.closest('[data-platform-panel]');
    if (!parentPanel || !parentPanel.hidden) {
      this.startAutoplay();
    }
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    this.#controller?.abort();
  }

  get #isAutoplayConfigured() {
    return this.dataset.autoplay === 'true' && this.#slides.length > 1;
  }

  get #autoplaySpeed() {
    const seconds = parseInt(this.dataset.autoplaySpeed, 10);
    return (isNaN(seconds) || seconds < 1 ? 5 : seconds) * 1000;
  }

  startAutoplay() {
    if (!this.#isAutoplayConfigured) return;
    this.pauseAutoplay();

    const parentPanel = this.closest('[data-platform-panel]');
    if (parentPanel && parentPanel.hidden) return;
    if (document.visibilityState === 'hidden') return;

    this.#timer = window.setInterval(() => {
      this.#show(this.#index + 1);
    }, this.#autoplaySpeed);
  }

  pauseAutoplay() {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  #onClick = (event) => {
    const stepButton = event.target.closest('[data-slide-step]');
    if (stepButton) {
      this.#show(this.#index + Number(stepButton.dataset.slideStep));
    }
  };

  #onKeyDown = (event) => {
    if (!event.target.closest('[data-slide-step]')) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const step = (event.key === 'ArrowRight' ? 1 : -1) * (this.#isRtl ? -1 : 1);
    this.#show(this.#index + step);
  };

  #onMouseEnter = () => {
    this.pauseAutoplay();
  };

  #onMouseLeave = () => {
    this.startAutoplay();
  };

  #onFocusIn = () => {
    this.pauseAutoplay();
  };

  #onFocusOut = (event) => {
    if (!this.contains(event.relatedTarget)) {
      this.startAutoplay();
    }
  };

  #onTouchStart = (event) => {
    const touch = event.touches[0];
    this.#touchStartX = touch.clientX;
    this.#touchStartY = touch.clientY;
  };

  #onTouchEnd = (event) => {
    if (this.#touchStartX === null || this.#touchStartY === null) return;
    const touch = event.changedTouches[0];
    const diffX = touch.clientX - this.#touchStartX;
    const diffY = touch.clientY - this.#touchStartY;
    this.#touchStartX = null;
    this.#touchStartY = null;

    if (Math.abs(diffX) > 35 && Math.abs(diffX) > Math.abs(diffY)) {
      const step = (diffX > 0 ? -1 : 1) * (this.#isRtl ? -1 : 1);
      this.#show(this.#index + step);
    }
  };

  #onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.pauseAutoplay();
    } else {
      this.startAutoplay();
    }
  };

  #show(index, { immediate = false } = {}) {
    if (!this.#slides.length) return;

    this.#index = (index + this.#slides.length) % this.#slides.length;

    if (this.#track) {
      if (immediate) {
        this.#track.style.transition = 'none';
      }
      const direction = this.#isRtl ? 1 : -1;
      const offset = direction * this.#index * 100;
      this.#track.style.transform = `translate3d(${offset}%, 0, 0)`;

      if (immediate) {
        void this.#track.offsetHeight;
        this.#track.style.transition = '';
      }
    }

    this.#slides.forEach((slide, position) => {
      const isActive = position === this.#index;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', String(!isActive));
      slide.tabIndex = isActive ? 0 : -1;
    });

    if (!immediate && this.#isAutoplayConfigured) {
      this.startAutoplay();
    }
  }
}

if (!customElements.get('site-warehouse-carousel')) {
  customElements.define('site-warehouse-carousel', SiteWarehouseCarousel);
}
